import { type ElementType, useState } from 'react';

import type { PlanningEntityType } from 'generated';

import type { Event } from '../../types';

import {
  GRAY_LIGHT_SECONDARY,
  ORANGE,
  PRIMARY,
  RED,
  WHITE
} from 'constants/colors';

import {
  getActivityTypeColor,
  getAddressNotFound,
  getHasWarnings
} from '../../utils';
import { brightness } from 'utils/misc';

import { Box, type SxProps, Tooltip } from '@mui/material';
import { EventTooltip } from '../../components/EventTooltip';
import ContextMenuContainer from '../../components/ContextMenu/ContextMenuContainer';

import { getZoomLevelProps } from '../../components/ActivityCard/activity-zoom-level-props';
import { useNavParams, useSelector } from 'hooks';
import { useIsAnyDragActive } from '../../hooks/useIsAnyDragActive';
import { View } from '../../enums';
import {
  AddressSection,
  NameSection,
  TimeSection
} from '../../components/ActivityCard/components';
import { SignalRow } from '../../components/SignalRow';
import type { CopyModeReturn } from '../../hooks/useCopyMode';
import SecondNameSection from '../../components/ActivityCard/components/SecondNameSection';
import dayjs from 'dayjs';
import {
  StyledBoxFlexColumnNoGap,
  StyledBoxFlexStartSmallGap
} from 'components/shared/Box/Box.styles';
import { ZoomLevel } from '../../components/Header/ZoomLevelMenu.enums';

// NOTE: This is the Pragmatic DnD equivalent of ActivityCard.
// It is intentionally free of @dnd-kit dependencies.
// See CardsViewPragmatic/README.md for the full migration strategy.

type CardProps = Omit<
  Event,
  | 'droppable'
  | 'category'
  | 'date'
  | 'end'
  | 'status'
  | 'isSubmittable'
  | 'isApprovable'
  | 'isDeletable'
> & {
  planningEntityId: string;
  planningEntityType: PlanningEntityType;
};

export interface ActivityCardPragmaticProps extends CardProps {
  component?: ElementType<any>;
  copyMode?: CopyModeReturn;
  setDragDisabled?: (disabled: boolean) => void;
  selected: boolean;
  hovered?: boolean;
  setHovered?: (hovered: boolean) => void;
  isTaskList?: boolean;
  sx?: SxProps;
}

export const ActivityCardPragmatic = (props: ActivityCardPragmaticProps) => {
  'use memo';
  const {
    appointmentGrouping,
    id,
    organizationalUnit,
    activity,
    workPlace,
    project,
    employeeType,
    planningEntityType,
    clients,
    copyMode,
    employees,
    component = 'div',
    noteInternal,
    durationInSeconds,
    warnings,
    isAbsent,
    isAssigned,
    isEditable,
    isCreditable,
    isCredit,
    isOpen,
    isRecurring,
    setDragDisabled,
    scheduleStart,
    start,
    selected,
    timeWindow,
    location,
    schedulingLabel,
    sx,
    hovered,
    linkedDutyCode,
    isTaskList = false,
    setHovered
  } = props;
  const {
    startsTooEarly,
    startsTooLate,
    isInSync: isInSyncWithEcd,
    hasOverlap,
    isOverQualified: overQualified,
    isUnderQualified: underQualified,
    excludedEmployeeClientCombination
  } = warnings || {};
  const client = clients?.[0];
  const employee = employees?.[0];

  const address = client?.address
    ? `${client.address.streetName} ${client.address.houseNumber}, ${client.address.postalCode} ${client.address.city}`
    : '';

  const addressNotFound = getAddressNotFound(location, activity);
  const cardTypeColor = getActivityTypeColor(!!client, isAbsent);
  const { isCopyMode, copyId, pasteAppointment } = copyMode ?? {};

  const { params } = useNavParams();
  const { defaultZoomLevel } = useSelector((state) => state.preferences);
  const zoomLevel = defaultZoomLevel ?? ZoomLevel.STANDARD;

  const [contextMenuActive, setContextMenuActive] = useState<boolean>(false);
  // HTML5 DnD captures the pointer and never fires `mouseleave`, so suppress
  // hover tooltip globally during any drag — otherwise it stays stuck.
  const isAnyDragActive = useIsAnyDragActive();
  const allowToolTip: boolean =
    !!hovered && !contextMenuActive && !isCopyMode && !isAnyDragActive;

  const allowContextMenu = !isCopyMode;

  const enableCardZoomLevels: boolean =
    params?.view === View.Cards ||
    params?.view === View.CardsPragmatic ||
    params?.view === View.Roster ||
    params?.view === View.RosterPragmatic ||
    params?.view === View.Timeline ||
    params?.view === View.TimelinePragmatic;

  const { showAddress, showSignals, signalThreshold, titleLineClamp } =
    getZoomLevelProps(enableCardZoomLevels, zoomLevel);

  const hasWarnings = getHasWarnings({
    startsTooEarly,
    startsTooLate,
    isInSyncWithEcd,
    excludedEmployeeClientCombination,
    isUrgent: !!timeWindow?.isUrgent,
    hasOverlap
  });

  return (
    <Tooltip
      enterNextDelay={500}
      disableHoverListener={!allowToolTip}
      data-selected={selected ? '' : undefined}
      sx={{
        zIndex: 0,
        boxSizing: 'border-box',
        flexShrink: 0,
        borderColor: GRAY_LIGHT_SECONDARY,
        border:
          isCopyMode && id == copyId
            ? `2px dashed ${PRIMARY}`
            : '1px solid #EFEFEF',
        bgcolor: selected
          ? brightness(cardTypeColor, 90)
          : workPlace && isOpen
          ? '#fdefd6'
          : 'lightGray.main',
        overflow: 'hidden',
        borderLeft: isRecurring
          ? `4px solid ${cardTypeColor}`
          : `2px solid ${cardTypeColor}`,
        position: 'relative',
        cursor: isCopyMode && (project || workPlace) ? 'pointer' : 'auto',
        minHeight: zoomLevel !== ZoomLevel.COMPACT ? 50 : 'initial',
        '[data-drag-active] &[data-selected]': { opacity: 0.5 },
        ...(hasWarnings &&
          selected && {
            backgroundImage:
              'linear-gradient(rgba(244,23,16,0.15), rgba(244,23,16,0.15))'
          }),
        ...(sx ? sx : {})
      }}
      onClick={() => {
        if (isCopyMode && (project?.entityId || workPlace?.entityId)) {
          pasteAppointment?.({
            organizationalUnitId: organizationalUnit?.id,
            projectEntityId: project?.entityId,
            workPlaceEntityId: workPlace?.entityId,
            activityEntityId: activity ? activity?.entityId : undefined,
            startDate: start
          });
        }
      }}
      title={
        allowToolTip && (
          <EventTooltip
            event={props as unknown as Event}
            onMouseOver={() => {
              setHovered?.(false);
            }}
          />
        )
      }
    >
      <Box
        id={id}
        component={component}
        className={`${isAbsent ? 'absent-card ' : ''} __${id}__ __${dayjs(
          start
        ).format('YYYY-MM-DD')}__ activity-card `}
        sx={{
          boxSizing: 'border-box',
          flexShrink: 0,
          ...(isCopyMode &&
            id == copyId && { border: `2px dashed ${PRIMARY}` }),
          bgcolor: selected ? brightness(cardTypeColor, 90) : WHITE,
          overflow: 'hidden',
          boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
          borderLeft: isRecurring
            ? `4px solid ${cardTypeColor}`
            : `2px solid ${cardTypeColor}`,
          position: 'relative',
          cursor: (project || workPlace) && isCopyMode ? 'pointer' : 'auto',
          minHeight: zoomLevel !== ZoomLevel.COMPACT ? 50 : 'initial',
          marginBottom: isTaskList && isAbsent ? '8px' : 'initial',
          '&:hover': {
            ...((project || workPlace) &&
              isCopyMode &&
              id !== copyId && { outline: `2px dashed ${ORANGE}` })
          },
          ...(hasWarnings &&
            !selected && { backgroundColor: brightness(RED, 90) }),
          ...(hasWarnings &&
            selected && {
              backgroundImage:
                'linear-gradient(rgba(244,23,16,0.15), rgba(244,23,16,0.15))'
            }),
          ...(sx ? sx : {})
        }}
        onClick={() => {
          if (isCopyMode && (project?.entityId || workPlace?.entityId)) {
            pasteAppointment?.({
              organizationalUnitId: organizationalUnit?.id,
              ...(project?.entityId
                ? { projectEntityId: project?.entityId }
                : {}),
              ...(workPlace?.entityId
                ? { workPlaceEntityId: workPlace?.entityId }
                : {}),
              activityEntityId: activity ? activity?.entityId : undefined,
              startDate: start
            });
          }
        }}
      >
        <StyledBoxFlexStartSmallGap>
          <StyledBoxFlexColumnNoGap>
            <TimeSection
              durationInSeconds={durationInSeconds}
              isAbsent={isAbsent}
              project={project}
              start={start}
            />

            {showSignals && (
              <SignalRow
                schedulingLabel={schedulingLabel}
                addressNotFound={addressNotFound}
                noteInternal={noteInternal}
                excludedEmployeeClientCombination={
                  excludedEmployeeClientCombination
                }
                hasOverlap={hasOverlap}
                isAbsent={isAbsent}
                isInSyncWithEcd={isInSyncWithEcd}
                isRecurring={isRecurring}
                locationType={location?.locationType}
                overQualified={overQualified}
                selected={selected}
                signalThreshold={signalThreshold}
                startsTooEarly={startsTooEarly}
                startsTooLate={startsTooLate}
                timeWindow={timeWindow}
                underQualified={underQualified}
              />
            )}
          </StyledBoxFlexColumnNoGap>

          <StyledBoxFlexStartSmallGap>
            <StyledBoxFlexColumnNoGap>
              <NameSection
                planningEntityType={planningEntityType}
                activity={activity}
                client={client}
                isAbsent={isAbsent}
                titleLineClamp={titleLineClamp}
              />
              <SecondNameSection
                planningEntityType={planningEntityType}
                schedulingLabel={schedulingLabel}
                activity={activity}
                employee={employee}
                client={client}
                employeeType={employeeType}
                isAbsent={isAbsent}
                linkedDutyCode={linkedDutyCode}
              />
            </StyledBoxFlexColumnNoGap>
            {allowContextMenu && (
              <ContextMenuContainer
                eventId={id}
                scheduleStart={scheduleStart}
                clientId={client?.id}
                eventIsOpen={isOpen}
                hasAppointmentGrouping={!!appointmentGrouping}
                hasEmployee={!!employee}
                isAssigned={!!isAssigned}
                isAbsent={isAbsent}
                isEditable={isEditable}
                isCreditable={isCreditable}
                isCredit={isCredit}
                schedulingLabel={schedulingLabel}
                isProject={!!project}
                isWorkPlace={!!workPlace}
                onOpen={(open: boolean) => {
                  setContextMenuActive(open);
                  setDragDisabled?.(open);
                }}
                sx={{ position: 'absolute', top: 0, right: 0 }}
              />
            )}
          </StyledBoxFlexStartSmallGap>
        </StyledBoxFlexStartSmallGap>

        {/* BOTTOM ROW - ADDRESS DETAILS */}
        {showAddress && address && <AddressSection address={address} />}
      </Box>
    </Tooltip>
  );
};
